import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ShoppingCart, ListOrdered, User, RefreshCw, HelpCircle } from "lucide-react";
import api, { imageSrc } from "@/api";
import TopBar from "@/components/TopBar";
import OfferPopup from "@/components/OfferPopup";
import { useLang } from "@/context/LanguageContext";
import { toast } from "sonner";

export default function ShopkeeperHome() {
  const nav = useNavigate();
  const { t, lang, displayName } = useLang();
  const [reorders, setReorders] = useState([]);
  const [busy, setBusy] = useState(null);

  useEffect(() => {
    api.get("/shopkeeper/frequent-reorders").then(r => setReorders(r.data.items || [])).catch(() => {});
  }, []);

  const reorderAgain = async (item) => {
    setBusy(item.product_id);
    try {
      await api.post("/orders", {
        items: [{ product_id: item.product_id, name: item.name, quantity: item.last_qty }],
      });
      toast.success(t("order_sent"));
      nav("/shop/orders");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    } finally { setBusy(null); }
  };

  const buttons = [
    { key: "place", testid: "btn-place-order", icon: ShoppingCart, label: t("place_order"), to: "/shop/place", color: "bg-[#0055FF]" },
    { key: "orders", testid: "btn-my-orders", icon: ListOrdered, label: t("my_orders"), to: "/shop/orders", color: "bg-[#34C759]" },
    { key: "profile", testid: "btn-profile", icon: User, label: t("profile"), to: "/shop/profile", color: "bg-[#FF9500]" },
    { key: "contact", testid: "btn-contact-us", icon: HelpCircle, label: t("complaint_inquiry"), to: "/shop/contact", color: "bg-[#5856D6]" },
  ];

  const reorderLabel = lang === "ta" ? "மீண்டும் ஆர்டர் போடு" : "Reorder";

  return (
    <div className="min-h-screen bg-[#F5F5F7]">
      <OfferPopup />
      <TopBar title={t("app_name")} />
      <div className="max-w-3xl mx-auto p-5 sm:p-8 fade-up space-y-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          {buttons.map(b => (
            <button
              key={b.key}
              data-testid={b.testid}
              onClick={() => nav(b.to)}
              className="btn-massive aspect-[4/3] sm:aspect-square text-[#1D1D1F]"
            >
              <div className={`${b.color} w-20 h-20 rounded-3xl grid place-items-center shadow-md`}>
                <b.icon className="w-10 h-10 text-white" strokeWidth={2.2} />
              </div>
              <span className="heading-font text-2xl sm:text-3xl font-bold mt-2">{b.label}</span>
            </button>
          ))}
        </div>

        {reorders.length > 0 && (
          <div className="fade-up">
            <div className="flex items-center gap-2 mb-3 px-1">
              <RefreshCw className="w-5 h-5 text-[#0055FF]" />
              <h2 className="heading-font text-xl font-bold text-[#1D1D1F]">
                {lang === "ta" ? "உங்க அடிக்கடி வாங்குறவை" : "Frequently Reordered"}
              </h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {reorders.map(item => (
                <div key={item.product_id} className="bg-white rounded-3xl soft-shadow overflow-hidden border border-gray-100" data-testid={`reorder-card-${item.product_id}`}>
                  <div className="aspect-[5/3] bg-gray-100 overflow-hidden">
                    <img src={imageSrc(item.image_url)} alt={item.name} className="w-full h-full object-cover" />
                  </div>
                  <div className="p-4">
                    <div className="font-bold heading-font text-[#1D1D1F] truncate">{displayName(item)}</div>
                    <div className="text-xs text-[#86868B] mt-0.5 mb-3">
                      {item.last_qty} {t("boxes")}
                    </div>
                    <button
                      data-testid={`reorder-btn-${item.product_id}`}
                      onClick={() => reorderAgain(item)}
                      disabled={busy === item.product_id}
                      className="w-full bg-[#0055FF] hover:bg-[#0044CC] disabled:opacity-50 text-white rounded-2xl py-3 font-bold text-base flex items-center justify-center gap-2 shadow-sm"
                    >
                      <RefreshCw className={`w-4 h-4 ${busy === item.product_id ? "animate-spin" : ""}`} />
                      {reorderLabel}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
